import { workspaceFetch } from '../../../../shared/platform/business-workspaces';
/**
 * The live adapter for the synthetic 05 LocalAsset workflow. LOCAL SYNTHETIC / NON-OPERATIONAL.
 *
 * WHY IT REFUSES ANY ORIGIN THAT IS NOT A ROOT LOOPBACK ADDRESS. This is a synthetic management
 * write surface. It exists to be exercised from a developer's own machine against a developer's own
 * host, and it is not authorized to be reachable from, or to reach, anything else. Enforcing that in
 * the client means a misconfigured `baseOrigin` — a public address, a host with a path, a URL
 * carrying credentials — fails at construction rather than silently sending a session token
 * somewhere it should not go.
 *
 * WHAT IT NEVER DOES.
 *   - It never puts the session in a URL. A credential in a query string lands in access logs, proxy
 *     logs and browser history; it travels in a header.
 *   - It never retries. A retry with a fresh idempotency key would be a NEW effect, and a retry with
 *     the same key is the caller's decision to make, not the transport's.
 *   - It never turns a transport failure into a business outcome. No response means UNREACHABLE; a
 *     response it cannot parse means CONTRACT_DEFECT. Neither is ever reported as a refusal or a
 *     success.
 *   - It never sends credentials (`credentials: 'omit'`) and never follows a redirect
 *     (`redirect: 'error'`): a redirect is how a request ends up at an origin the caller did not
 *     choose.
 */
import type { ServerOutcome } from '../../../../shared/product-review/adapters/product-review-port';
import type {
  AssetReviewCaseView,
  AssetScopeView,
  AssetView,
  ExactPublicationView,
  LocalAssetWorkflowPort,
  PublicationView,
  PublishedProductView,
  SubmissionView,
} from './local-asset-workflow-port';
import {
  declarationBody,
  parseAsset,
  parseAssetList,
  parseAssetReviewList,
  parseExactPublication,
  parseProductVersionList,
  parsePublication,
  parseScope,
  parseServerError,
  parseSubmission,
} from './local-asset-workflow-wire';

export const SESSION_HEADER = 'X-Rescue-Synthetic-Session';
export const CSRF_HEADER = 'X-Rescue-CSRF';
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

const MANAGEMENT_ROOT = '/management/local-asset';

interface LiveLocalAssetOptions {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
  readonly csrf?: () => string;
}

function requireRootLoopbackOrigin(baseOrigin: string): string {
  // Check the supplied path BEFORE URL normalization can erase dot segments.
  if (!/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(baseOrigin)) {
    throw new Error('A root local HTTP origin is required');
  }
  const url = new URL(baseOrigin);
  const ipv4 = url.hostname.split('.');
  const loopback =
    url.hostname === 'localhost' ||
    url.hostname === '[::1]' ||
    (ipv4.length === 4 &&
      ipv4[0] === '127' &&
      ipv4.every((part) => /^\d+$/.test(part) && Number(part) <= 255));
  if (!loopback || url.username || url.password || url.search || url.hash || url.pathname !== '/') {
    throw new Error('A root local HTTP origin is required');
  }
  return url.origin;
}

export function createLiveLocalAssetWorkflowAdapter({
  baseOrigin,
  fetchImpl,
  csrf,
}: LiveLocalAssetOptions): LocalAssetWorkflowPort {
  const origin = requireRootLoopbackOrigin(baseOrigin);
  const send = fetchImpl ?? workspaceFetch;

  /**
   * One request, one outcome. `parse` is only ever called for a 2xx response, so a refusal body is
   * never fed to a success parser and a success body is never fed to the error parser.
   */
  async function call<T>(
    method: 'GET' | 'POST',
    path: string,
    session: string,
    parse: (body: unknown) => T,
    idempotencyKey?: string,
    body?: Record<string, unknown>,
  ): Promise<ServerOutcome<T>> {
    const headers: Record<string, string> = { [SESSION_HEADER]: session };
    if (method === 'POST' && csrf) headers[CSRF_HEADER] = csrf();
    if (idempotencyKey !== undefined) headers[IDEMPOTENCY_HEADER] = idempotencyKey;
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    let response: Response;
    try {
      // The body is attached only when there is one: the project sets `exactOptionalPropertyTypes`,
      // so `body: undefined` is not the same as an absent body, and a GET carrying an explicit
      // `undefined` body is a different request from a GET with none.
      const init: RequestInit = {
        method,
        headers,
        credentials: 'omit',
        redirect: 'error',
        cache: 'no-store',
      };
      if (body !== undefined) init.body = JSON.stringify(body);
      response = await send(`${origin}${path}`, init);
    } catch (failure) {
      return {
        kind: 'UNREACHABLE',
        detail: failure instanceof Error ? failure.message : 'the request could not be sent',
      };
    }

    let parsedBody: unknown = null;
    let bodyIsJson = false;
    try {
      const raw = await response.text();
      if (raw.length > 0) {
        parsedBody = JSON.parse(raw);
        bodyIsJson = true;
      }
    } catch {
      bodyIsJson = false;
    }

    if (!response.ok) {
      if (!bodyIsJson) {
        return {
          kind: 'CONTRACT_DEFECT',
          detail: `HTTP ${response.status} with a body this client cannot read`,
        };
      }
      try {
        const error = parseServerError(parsedBody);
        if (error.code === 'UPSTREAM_OUTCOME_UNKNOWN' || error.code === 'PERSISTENCE_UNAVAILABLE') {
          return { kind: 'UNREACHABLE', detail: `HTTP ${response.status}: 写入结果未确认` };
        }
        return {
          kind: 'REFUSED',
          httpStatus: response.status,
          code: error.code,
          field: error.field,
        };
      } catch (defect) {
        return {
          kind: 'CONTRACT_DEFECT',
          detail: defect instanceof Error ? defect.message : 'unreadable error body',
        };
      }
    }

    if (!bodyIsJson) {
      return { kind: 'CONTRACT_DEFECT', detail: 'a successful response carried no JSON body' };
    }
    try {
      return { kind: 'OK', value: parse(parsedBody) };
    } catch (defect) {
      return {
        kind: 'CONTRACT_DEFECT',
        detail: defect instanceof Error ? defect.message : 'unreadable response body',
      };
    }
  }

  const assetPath = (assetId: string) => `${MANAGEMENT_ROOT}/assets/${encodeURIComponent(assetId)}`;

  return {
    scope(input) {
      return call<AssetScopeView>('GET', `${MANAGEMENT_ROOT}/scope`, input.session, parseScope);
    },
    ownAssets(input) {
      return call<readonly AssetView[]>(
        'GET',
        `${MANAGEMENT_ROOT}/assets`,
        input.session,
        parseAssetList,
      );
    },
    catalog(input) {
      return call<readonly AssetView[]>(
        'GET',
        `${MANAGEMENT_ROOT}/catalog`,
        input.session,
        parseAssetList,
      );
    },
    productVersions(input) {
      return call<readonly PublishedProductView[]>(
        'GET',
        `${MANAGEMENT_ROOT}/product-versions`,
        input.session,
        parseProductVersionList,
      );
    },
    assetReviews(input) {
      return call<readonly AssetReviewCaseView[]>(
        'GET',
        `${assetPath(input.assetId)}/reviews`,
        input.session,
        parseAssetReviewList,
      );
    },
    create(input) {
      return call<AssetView>(
        'POST',
        `${MANAGEMENT_ROOT}/assets`,
        input.session,
        parseAsset,
        input.idempotencyKey,
        declarationBody(input.declaration),
      );
    },
    read(input) {
      return call<AssetView>('GET', assetPath(input.assetId), input.session, parseAsset);
    },
    revise(input) {
      return call<AssetView>(
        'POST',
        `${assetPath(input.assetId)}/revisions`,
        input.session,
        parseAsset,
        input.idempotencyKey,
        { expectedVersion: input.expectedVersion, ...declarationBody(input.declaration) },
      );
    },
    submit(input) {
      return call<SubmissionView>(
        'POST',
        `${assetPath(input.assetId)}/submissions`,
        input.session,
        parseSubmission,
        input.idempotencyKey,
        { expectedVersion: input.expectedVersion },
      );
    },
    resume(input) {
      return call<AssetView>(
        'POST',
        `${assetPath(input.assetId)}/resumptions`,
        input.session,
        parseAsset,
        input.idempotencyKey,
        { expectedVersion: input.expectedVersion, decisionId: input.decisionId },
      );
    },
    publish(input) {
      return call<PublicationView>(
        'POST',
        `${assetPath(input.assetId)}/publications`,
        input.session,
        parsePublication,
        input.idempotencyKey,
        {
          submission: {
            country: input.submission.country,
            agency: input.submission.agency,
            revision: input.submission.revision,
            digest: input.submission.digest,
          },
          decisionId: input.decisionId,
        },
      );
    },
    readExact(input) {
      const query = new URLSearchParams({
        country: input.country,
        agency: input.agency,
        digest: input.digest,
      });
      return call<ExactPublicationView>(
        'GET',
        `${assetPath(input.assetId)}/published/${input.publishedVersion}?${query.toString()}`,
        input.session,
        parseExactPublication,
      );
    },
  };
}
